import React, { useState, useEffect } from 'react';
import { MediaViewer } from '../media/MediaViewer';
import { FileType, getFileType } from '../../utils/fileTypes';

interface MediaViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  fileName: string;
  filePath: string;
  onSave?: (content: string) => void;
}

export const MediaViewerModal: React.FC<MediaViewerModalProps> = ({
  isOpen,
  onClose,
  fileName,
  filePath,
  onSave
}) => {
  const [content, setContent] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const fileType = getFileType(fileName);
  const needsContent = fileType === FileType.TEXT || fileType === FileType.JSON;

  useEffect(() => {
    if (isOpen && needsContent) {
      fetchFileContent();
    }
  }, [isOpen, filePath, needsContent]);

  const fetchFileContent = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://api.hi-si.ai';
      const isCurriculumLessonJson = /\/content\/curriculum\/baseline-en-US\/drafts\/content\/[^/]+\/day\d+\.json$/.test(filePath);
      const fileUrl = `${API_BASE_URL}/api/file/content?path=${encodeURIComponent(filePath)}`;
      const lessonUrl = `${API_BASE_URL}/api/curriculum/lesson?path=${encodeURIComponent(filePath)}`;
      
      console.log('🔍 CURRICULUM DEBUG: API_BASE_URL:', API_BASE_URL);
      console.log('🔍 CURRICULUM DEBUG: File path:', filePath);
      console.log('🔍 CURRICULUM DEBUG: isCurriculumLessonJson:', isCurriculumLessonJson);

      // Strategy: for curriculum lessons, try lesson endpoint first, then file endpoint.
      // For all other files, try file endpoint first.
      const primaryUrl = isCurriculumLessonJson ? lessonUrl : fileUrl;
      const secondaryUrl = isCurriculumLessonJson ? fileUrl : lessonUrl;

      const fetchWithNoCache = async (u: string) => {
        const resp = await fetch(u, {
          cache: 'no-store',
          credentials: 'include'
        });
        return resp;
      };

      console.log('🔍 CURRICULUM DEBUG: Primary fetch:', primaryUrl);
      let resp = await fetchWithNoCache(primaryUrl);
      console.log('🔍 CURRICULUM DEBUG: Primary status:', resp.status);

      let text = '';
      if (resp.ok) {
        text = await resp.text();
        console.log('🔍 CURRICULUM DEBUG: Primary length:', text.length);
        console.log('🔍 CURRICULUM DEBUG: Primary preview:', text.substring(0, 200));
      }

      const shouldFallback = !resp.ok || !text || text.trim().length === 0 || text.trim() === '{}';

      if (shouldFallback) {
        console.log('🔍 CURRICULUM DEBUG: Fallback fetch:', secondaryUrl);
        const resp2 = await fetchWithNoCache(secondaryUrl);
        console.log('🔍 CURRICULUM DEBUG: Fallback status:', resp2.status);
        if (!resp2.ok) {
          throw new Error(`Failed to load file content: ${resp.status} ${resp.statusText}`);
        }
        const text2 = await resp2.text();
        console.log('🔍 CURRICULUM DEBUG: Fallback length:', text2.length);
        console.log('🔍 CURRICULUM DEBUG: Fallback preview:', text2.substring(0, 200));
        setContent(text2);
        return;
      }

      setContent(text);
    } catch (err) {
      console.error('🔍 CURRICULUM DEBUG: Fetch error:', err);
      setError(err instanceof Error ? err.message : 'Failed to load file');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async (updatedContent: string) => {
    // If parent provided a save handler, use it; otherwise write via API
    if (onSave) {
      await onSave(updatedContent);
      return;
    }
    try {
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://api.hi-si.ai';
      const isCurriculumLessonJson = /\/content\/curriculum\/baseline-en-US\/drafts\/content\/[^/]+\/day\d+\.json$/.test(filePath);
      if (isCurriculumLessonJson) {
        const resp = await fetch(`${API_BASE_URL}/api/curriculum/lesson`, {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ path: filePath, content: updatedContent })
        });
        if (!resp.ok) throw new Error('Failed to save lesson');
      } else {
        const resp = await fetch(`${API_BASE_URL}/api/file/save`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ path: filePath, content: updatedContent })
        });
        if (!resp.ok) throw new Error('Failed to save file');
      }
      setContent(updatedContent);
    } catch (err) {
      console.error('Save error:', err);
      setError(err instanceof Error ? err.message : 'Failed to save');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      <div 
        className={`bg-white rounded-lg shadow-xl max-w-6xl max-h-[90vh] overflow-hidden ${
          fileType === FileType.VIDEO ? 'w-full h-full' : 'w-full'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {fileType !== FileType.VIDEO && (
          <div className="flex items-center justify-between p-4 border-b">
            <h2 className="text-lg font-medium text-gray-900">
              {fileName}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-xl"
            >
              ✕
            </button>
          </div>
        )}
        
        <div className={`${fileType === FileType.VIDEO ? 'h-full' : 'max-h-[80vh] overflow-auto'}`}>
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="text-2xl mb-2">⏳</div>
                <p className="text-gray-500">Loading file...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="text-red-500 text-2xl mb-2">❌</div>
                <p className="text-red-700">{error}</p>
                <button
                  onClick={fetchFileContent}
                  className="mt-2 px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                  Retry
                </button>
              </div>
            </div>
          ) : (
            <MediaViewer
              fileName={fileName}
              filePath={filePath}
              content={content}
              onSave={handleSave}
              onClose={fileType === FileType.VIDEO ? onClose : undefined}
            />
          )}
        </div>
      </div>
    </div>
  );
};